import { AixGlobalCommandBar } from "@aix/ui"
import { createRootRoute, createRoute, createRouter, Link, Outlet, useLocation } from "@tanstack/react-router"
import { TanStackRouterDevtools } from "@tanstack/router-devtools"
import { ChevronLeft, ChevronRight, Menu, Star, X } from "lucide-react"
import * as Sentry from "@sentry/react"
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react"

const DevModePanel = lazy(() => import("./components/DevModePanel"))

import { NavigationCommandItems } from "./components/CommandBar/NavigationCommandItems"
import DarkModeToggle from "./components/DarkModeToggle"
import NewsletterEditor from "./components/Newsletter/NewsletterEditor"
import ProtectedRoute from "./components/ProtectedRoute"
import PublicRoute from "./components/PublicRoute"
import { getNavigationForUser } from "./config/navigationConfig"
import { routeRegistry } from "./config/routeRegistry"
import { useGlobalShortcuts } from "./hooks/useGlobalShortcuts"
import { useAuth } from "./lib/auth-context"
import { ChatProvider } from "./lib/chat-context-telegram"
import { trpc } from "./lib/trpc"
import Tools from "./pages/Tools"
import AccessDeniedScreen from "./Screens/AccessDeniedScreen"
import AIChat from "./Screens/AIChat"
import AIChatHubScreen from "./Screens/AIChatHubScreen/AIChatHubScreen"
import AIProjectsScreen from "./Screens/AIProjectsScreen/AIProjectsScreen"
import AuthCallbackScreen from "./Screens/AuthCallbackScreen"
import CliAuthScreen from "./Screens/CliAuthScreen"
import AuthScreen from "./Screens/AuthScreen"
import BookReaderScreen from "./Screens/BookReaderScreen"
import BrainScreen from "./Screens/BrainScreen"
import CalendarParser from "./Screens/CalendarParser"
import CoachOSScreen from "./Screens/CoachOSScreen/CoachOSScreen"
import CoachDetailScreen from "./Screens/CoachOSScreen/CoachDetailScreen"
import CoachTemplateDetailScreen from "./Screens/CoachOSScreen/CoachTemplateDetailScreen"
import WeeklyReviewScreen from "./Screens/CoachOSScreen/WeeklyReviewScreen"
import DatabrainScreen from "./Screens/DatabrainScreen/DatabrainScreen"
import DiagrammerScreen from "./Screens/DiagrammerScreen/DiagrammerScreen"
import ChildrenBooksScreen from "./Screens/ChildrenBooksScreen"
import ContentIngestionScreen from "./Screens/ContentIngestionScreen"
import ContentManagementScreen from "./Screens/ContentManagementScreen"
import Analytics from "./Screens/CRM/Analytics"
import CampaignDetail from "./Screens/CRM/CampaignDetail"
import CampaignsList from "./Screens/CRM/CampaignsList"
import ContactDetail from "./Screens/CRM/ContactDetail"
import ContactsList from "./Screens/CRM/ContactsList"
import ConversationDetail from "./Screens/CRM/ConversationDetail"
import ConversationInbox from "./Screens/CRM/ConversationInbox"
import CRMDashboard from "./Screens/CRM/CRMDashboard"
import CRMLayout from "./Screens/CRM/CRMLayout"
import EmailAccounts from "./Screens/CRM/EmailAccounts"
import StartConversation from "./Screens/CRM/StartConversation"
import TemplatesLibrary from "./Screens/CRM/TemplatesLibrary"
import CronScreen from "./Screens/CronScreen"
import ElectronNativeScreen from "./Screens/ElectronNativeScreen"
import { EASDashboardScreen } from "./Screens/EASDashboardScreen/EASDashboardScreen"
import { ProjectDashboard } from "./Screens/EASDashboardScreen/ProjectDashboard"
import { EmailMarketingScreen } from "./Screens/EmailMarketingScreen/EmailMarketingScreen"
import HomeDashboardScreen from "./Screens/HomeDashboardScreen/HomeDashboardScreen"
import ImageConverter from "./Screens/ImageConverter"
import IntegrationsScreen from "./Screens/IntegrationsScreen"
import { MetaAdsScreen } from "./Screens/MetaAdsScreen/MetaAdsScreen"
import MetricsScreen from "./Screens/MetricsScreen"
import MoviesScreen from "./Screens/MoviesScreen/MoviesScreen"
import MultiLanguageTestAppScreen from "./Screens/MultiLanguageTestAppScreen/MultiLanguageTestAppScreen"
import MashupScreen from "./Screens/MashupScreen/MashupScreen"
import MusicCatalogScreen from "./Screens/MusicCatalogScreen"
import MyWorkScreen from "./Screens/MyWorkScreen"
import NewsletterScreen from "./Screens/NewsletterScreen"
import { NotificationScreen } from "./Screens/NotificationScreen/NotificationScreen"
import ReportsScreen from "./Screens/ReportsScreen/ReportsScreen"
import { parseReportsSearch } from "./Screens/ReportsScreen/reportingValidation"
import ScrapingScreen from "./Screens/ScrapingScreen"
import SEOScreen from "./Screens/SEOScreen"
import SettingsScreen from "./Screens/SettingsScreen"
import SignOutScreen from "./Screens/SignOutScreen"
import SlackCallbackScreen from "./Screens/SlackCallbackScreen"
import SportsWordleScreen from "./Screens/SportsWordleScreen"
import TrackablesScreen from "./Screens/TrackablesScreen/TrackablesScreen"
import UserManagementScreen from "./Screens/UserManagementScreen"
import JourneysListScreen from "./Screens/OnboardingScreen/JourneysListScreen"
import JourneyDetailScreen from "./Screens/OnboardingScreen/JourneyDetailScreen"
import VideoPlayerScreen from "./Screens/OnboardingScreen/VideoPlayerScreen"
import AdminVideosScreen from "./Screens/OnboardingScreen/AdminVideosScreen"
import AdminJourneysScreen from "./Screens/OnboardingScreen/AdminJourneysScreen"
import AdminQuestionsScreen from "./Screens/OnboardingScreen/AdminQuestionsScreen"
import { CompaniesScreen } from "./Screens/CompaniesScreen/CompaniesScreen"
import { CompanyDetailScreen } from "./Screens/CompaniesScreen/CompanyDetailScreen"
import MonitorsScreen from "./Screens/MonitorsScreen/MonitorsScreen"
import ScenarioDetailScreen from "./Screens/MonitorsScreen/ScenarioDetailScreen"
import IncidentsScreen from "./Screens/IncidentsScreen/IncidentsScreen"
import IncidentDetailScreen from "./Screens/IncidentsScreen/IncidentDetailScreen"
import OnCallScreen from "./Screens/OnCallScreen/OnCallScreen"
import StatusPage from "./Screens/StatusPage/StatusPage"
import AiGatewayScreen from "./Screens/AiGatewayScreen"
import LLMPlaygroundScreen from "./Screens/LLMPlaygroundScreen/LLMPlaygroundScreen"
import AuraScreen from "./Screens/AuraScreen/AuraScreen"
import AuraEditorScreen from "./Screens/AuraScreen/AuraEditorScreen"
import AuraTemplatesScreen from "./Screens/AuraScreen/AuraTemplatesScreen"
import AuraTemplateBuilderScreen from "./Screens/AuraScreen/AuraTemplateBuilderScreen"
import AuditLogsScreen from "./Screens/AuditLogsScreen"
import SecretsScreen from "./Screens/SecretsScreen"
import UptimeMonitorDetailScreen from "./Screens/UptimeMonitorsScreen/UptimeMonitorDetailScreen"
import ProductPulseScreen from "./Screens/ProductPulseScreen/ProductPulseScreen"
import UptimeMonitorsScreen from "./Screens/UptimeMonitorsScreen/UptimeMonitorsScreen"
import VendorStatusScreen from "./Screens/VendorStatusScreen/VendorStatusScreen"
import AdminSigninScreen from "./Screens/AdminSigninScreen"
import AgentsCockpitScreen from "./Screens/AgentsScreen/AgentsCockpitScreen"
import AgentsScreen from "./Screens/AgentsScreen/AgentsScreen"
import AgentsRunsHistoryScreen from "./Screens/AgentsScreen/AgentsRunsHistoryScreen"
import DeployFeedScreen from "./Screens/DeployFeedScreen/DeployFeedScreen"
import EvalsScreen from "./Screens/EvalsScreen/EvalsScreen"
import ShraysHobbiesScreen from "./Screens/ShraysHobbiesScreen"
import { LinkPacksScreen } from "./Screens/LinkPacksScreen/LinkPacksScreen"
import { LinkPackDetailScreen } from "./Screens/LinkPacksScreen/LinkPackDetailScreen"

// CRM Screens
// Old hardcoded navigation removed - now using permission-aware navigation from config

// Starred items localStorage utilities
const STARRED_ITEMS_KEY = "aix-starred-items"

interface StarredItem {
  to: string
  label: string
  icon?: string
}

const getStarredItems = (): StarredItem[] => {
  try {
    const stored = localStorage.getItem(STARRED_ITEMS_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

const setStarredItems = (items: StarredItem[]) => {
  localStorage.setItem(STARRED_ITEMS_KEY, JSON.stringify(items))
}

const toggleStarredItem = (item: StarredItem) => {
  const starred = getStarredItems()
  const existingIndex = starred.findIndex((s) => s.to === item.to)

  if (existingIndex >= 0) {
    // Remove from starred
    starred.splice(existingIndex, 1)
  } else {
    // Add to starred
    starred.push(item)
  }

  setStarredItems(starred)
  return starred
}

const isItemStarred = (to: string): boolean => {
  const starred = getStarredItems()
  return starred.some((item) => item.to === to)
}

// Star icon component
const StarIcon = ({
  filled,
  onClick,
  className = "w-4 h-4",
}: {
  filled: boolean
  onClick: (e: React.MouseEvent) => void
  className?: string
}) => (
  <button
    onClick={onClick}
    className={`p-0.5 rounded hover:bg-sidebar-accent transition-colors ${className}`}
    title={filled ? "Remove from starred" : "Add to starred"}
  >
    <Star className={`w-3.5 h-3.5 ${filled ? "fill-yellow-400 text-yellow-400" : "text-sidebar-foreground"}`} />
  </button>
)

// Sidebar component
function Sidebar({ isCollapsed, onToggleCollapse }: { isCollapsed: boolean; onToggleCollapse: () => void }) {
  const location = useLocation()
  const { user, signOut, loading } = useAuth()

  // Get user access information
  const { data: userAccessData } = trpc.accessControl.getUserCategoryAccess.useQuery(undefined, {
    enabled: !!user,
  })

  // // Get permission-aware navigation - memoize user categories to prevent array recreation
  const userCategories = useMemo(
    () => (userAccessData?.success ? userAccessData.access.categories : []),
    [userAccessData?.success, JSON.stringify(userAccessData?.access?.categories)],
  )
  const isSuperAdmin = userAccessData?.success ? userAccessData.user.isSuperAdmin : false

  // Memoize navigation data to prevent unnecessary re-renders
  const { permissionAwareCategories, permissionAwareSystemItems } = useMemo(() => {
    const { categories, systemItems } = getNavigationForUser(userCategories, isSuperAdmin)
    return { permissionAwareCategories: categories, permissionAwareSystemItems: systemItems }
  }, [userCategories, isSuperAdmin])

  // Fallback navigation when not loaded
  const fallbackSystemItems = [{ to: "/", label: "📊 Status" }]

  // Load expanded categories from localStorage and auto-expand categories with active routes
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(() => {
    const saved = localStorage.getItem("expandedCategories")
    return saved ? new Set(JSON.parse(saved)) : new Set(["ai-tools"]) // Default expand ai-tools
  })

  // Update expanded categories when location changes to auto-expand categories with active routes
  useEffect(() => {
    // Find categories that contain the active route
    const activeCategoryIds = new Set<string>()
    permissionAwareCategories.forEach((category) => {
      const hasActiveRoute = category.items.some((item: any) => item.to === location.pathname)
      if (hasActiveRoute) {
        activeCategoryIds.add(category.category)
      }
    })

    // Only update if there are active categories to expand
    if (activeCategoryIds.size > 0) {
      setExpandedCategories((prev) => {
        const newSet = new Set(prev)
        let hasChanges = false
        activeCategoryIds.forEach((categoryId) => {
          if (!newSet.has(categoryId)) {
            newSet.add(categoryId)
            hasChanges = true
          }
        })
        return hasChanges ? newSet : prev
      })
    }
  }, [location.pathname, permissionAwareCategories])

  // Helper function for other parts of the component
  const getCategoriesWithActiveRoutes = () => {
    const activeCategoryIds = new Set<string>()
    permissionAwareCategories.forEach((category) => {
      const hasActiveRoute = category.items.some((item: any) => item.to === location.pathname)
      if (hasActiveRoute) {
        activeCategoryIds.add(category.category)
      }
    })
    return activeCategoryIds
  }

  // Starred items state
  const [starredItems, setStarredItems] = useState<StarredItem[]>(() => getStarredItems())

  // Track expanded sub-items (for nested navigation like "Video Onboarding")
  const [expandedSubItems, setExpandedSubItems] = useState<Set<string>>(() => {
    // Auto-expand sub-items if current path matches any subItem
    const expanded = new Set<string>()
    permissionAwareCategories.forEach((category) => {
      category.items.forEach((item: any) => {
        if (item.subItems && item.subItems.length > 0) {
          const hasActiveSubItem = item.subItems.some((subItem: any) => location.pathname === subItem.to)
          if (hasActiveSubItem) {
            expanded.add(item.label)
          }
        }
      })
    })
    return expanded
  })

  // Save expanded categories to localStorage
  useEffect(() => {
    localStorage.setItem("expandedCategories", JSON.stringify(Array.from(expandedCategories)))
  }, [expandedCategories])

  const handleSignOut = async () => {
    await signOut()
  }

  const toggleCategory = (categoryId: string) => {
    // Check if this category contains the active route
    const activeCategoryIds = getCategoriesWithActiveRoutes()

    // Prevent collapsing categories that contain the active route
    if (activeCategoryIds.has(categoryId)) {
      return // Don't allow collapsing categories with active routes
    }

    setExpandedCategories((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId)
      } else {
        newSet.add(categoryId)
      }
      return newSet
    })
  }

  const handleToggleStar = (item: { to: string; label: string; icon?: string }) => {
    const newStarredItems = toggleStarredItem(item)
    setStarredItems(newStarredItems)
  }

  const toggleSubItem = (label: string) => {
    setExpandedSubItems((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(label)) {
        newSet.delete(label)
      } else {
        newSet.add(label)
      }
      return newSet
    })
  }

  const renderNavigationItem = (item: any, depth = 0) => {
    // Handle items with subItems (nested navigation)
    if (item.subItems && item.subItems.length > 0) {
      const [emoji, ...textParts] = item.label.split(" ")
      const hasActiveSubItem = item.subItems.some((subItem: any) => location.pathname === subItem.to)
      const isSubExpanded = expandedSubItems.has(item.label)

      return (
        <li key={item.label} className="mb-1">
          <button
            onClick={() => toggleSubItem(item.label)}
            className={`w-full px-3 py-1.5 text-left flex items-center justify-between text-sm font-medium transition-colors rounded-md ${
              hasActiveSubItem
                ? "text-sidebar-primary bg-sidebar-accent"
                : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            }`}
          >
            <span className="flex items-center gap-2">
              <span>{emoji || item.icon || "📄"}</span>
              {!isCollapsed && <span>{textParts.join(" ") || item.label}</span>}
            </span>
            {!isCollapsed && (
              <svg
                className={`w-3.5 h-3.5 transition-transform duration-200 ${isSubExpanded ? "rotate-90" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </button>
          {!isCollapsed && isSubExpanded && (
            <ul className="mt-1 space-y-0.5 pl-6">
              {item.subItems.map((subItem: any) => renderNavigationItem(subItem, depth + 1))}
            </ul>
          )}
        </li>
      )
    }

    // Regular item with direct link
    const isActive = location.pathname === item.to
    const [emoji, ...textParts] = item.label.split(" ")
    const text = textParts.join(" ")
    const starred = isItemStarred(item.to)

    return (
      <li key={item.to}>
        <div
          className={`w-full rounded-md font-medium transition-all duration-200 text-sm ${
            isActive
              ? "bg-gradient-to-r from-sidebar-primary to-primary text-sidebar-primary-foreground shadow-md"
              : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-sm"
          } ${isCollapsed ? "flex justify-center items-center" : "flex justify-between items-center"}`}
        >
          <Link
            to={item.to}
            className={`${isCollapsed ? "px-3 py-1.5" : "flex-1 px-3 py-1.5 text-left"}`}
            title={isCollapsed ? item.label : undefined}
          >
            {isCollapsed ? <span className="text-lg">{emoji || item.icon || "📄"}</span> : item.label}
          </Link>
          {!isCollapsed && (
            <div className="pr-2">
              <StarIcon
                filled={starred}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleToggleStar({
                    to: item.to,
                    label: item.label,
                    icon: emoji || item.icon,
                  })
                }}
              />
            </div>
          )}
        </div>
      </li>
    )
  }

  const renderCategory = (category: any) => {
    const isExpanded = expandedCategories.has(category.category)
    const hasItems = category.items.length > 0
    const hasActiveRoute = category.items.some((item: any) => item.to === location.pathname)

    if (isCollapsed && hasItems) {
      // In collapsed mode, show items directly without category headers
      return (
        <li key={category.category} className="space-y-0.5">
          {category.items.map(renderNavigationItem)}
        </li>
      )
    }

    return (
      <li key={category.category} className="mb-4">
        <button
          onClick={() => hasItems && toggleCategory(category.category)}
          className={`w-full px-3 py-1.5 text-left flex items-center justify-between text-xs font-semibold text-sidebar-foreground uppercase tracking-wider transition-colors rounded-md ${
            hasItems && !hasActiveRoute
              ? "cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              : hasActiveRoute
                ? "cursor-default text-sidebar-primary" // Different styling for categories with active routes
                : "cursor-default"
          }`}
        >
          <span>{category.label}</span>
          {hasItems && (
            <svg
              className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""} ${
                hasActiveRoute ? "text-sidebar-primary" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
        </button>
        {hasItems && isExpanded && (
          <ul className="mt-1 space-y-0.5 pl-2">{category.items.map(renderNavigationItem)}</ul>
        )}
      </li>
    )
  }

  return (
    <aside
      className={`${
        isCollapsed ? "w-16" : "w-64"
      } bg-sidebar border-r border-sidebar-border flex flex-col h-full transition-all duration-300`}
    >
      <header
        className={`${
          isCollapsed ? "px-2" : "px-6"
        } py-4 flex-shrink-0 bg-sidebar/70 backdrop-blur-xs border-b border-sidebar-border relative`}
      >
        {!isCollapsed && (
          <h1 className="text-xl font-bold bg-gradient-to-r from-sidebar-primary to-primary bg-clip-text text-transparent">
            AIX Command Center
          </h1>
        )}

        {/* Collapse/Expand button */}
        <button
          onClick={onToggleCollapse}
          className={`absolute ${
            isCollapsed ? "right-2" : "right-4"
          } top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-sidebar-accent transition-colors hidden lg:flex items-center justify-center`}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4 text-sidebar-foreground" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-sidebar-foreground" />
          )}
        </button>
      </header>

      <nav className={`flex-1 overflow-y-auto min-h-0 sidebar-scroll ${isCollapsed ? "px-2" : "px-4"} py-3`}>
        {!user ? (
          <div className="text-center py-8 text-sidebar-foreground">
            {!isCollapsed && <div className="text-sm">Please sign in to view navigation</div>}
          </div>
        ) : !userAccessData ? (
          <div className="text-center py-8 text-sidebar-foreground">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sidebar-primary mx-auto mb-2" />
            {!isCollapsed && <div className="text-sm">Loading navigation...</div>}
          </div>
        ) : (
          <div className={`${isCollapsed ? "space-y-1" : "pb-4"}`}>
            {/* Starred Items Section */}
            {starredItems.length > 0 && (
              <div className="mb-6">
                {!isCollapsed && (
                  <div className="px-3 py-1.5 text-xs font-semibold text-sidebar-foreground uppercase tracking-wider mb-2 flex items-center">
                    <Star className="w-3 h-3 mr-1 fill-yellow-400 text-yellow-400" />
                    Starred
                  </div>
                )}
                <ul className="space-y-0.5">{starredItems.map(renderNavigationItem)}</ul>
                {!isCollapsed && <div className="border-b border-sidebar-border mt-4" />}
              </div>
            )}

            {/* Main Navigation Categories */}
            <ul className={`${isCollapsed ? "space-y-1" : ""}`}>
              {permissionAwareCategories.map(renderCategory)}

              {/* Show message if no categories available */}
              {permissionAwareCategories.length === 0 && !isCollapsed && (
                <li className="text-center py-8 text-sidebar-foreground">
                  <div className="text-sm">No navigation items available</div>
                  <div className="text-xs text-muted-foreground mt-1">Contact admin for access</div>
                </li>
              )}
            </ul>
          </div>
        )}
      </nav>

      {/* System/Admin Section */}
      <div
        className={`mt-auto ${
          isCollapsed ? "px-2" : "px-4"
        } pt-3 pb-3 border-t border-sidebar-border flex-shrink-0 bg-sidebar/30 backdrop-blur-xs`}
      >
        <div className="mb-3">
          {!isCollapsed && (
            <div className="px-3 py-1.5 text-xs font-semibold text-sidebar-foreground uppercase tracking-wider">
              System
            </div>
          )}
          <ul className="space-y-0.5">
            {(userAccessData ? permissionAwareSystemItems : fallbackSystemItems).map((item, index) => {
              const isActive = location.pathname === item.to
              const [emoji, ...textParts] = item.label.split(" ")
              const starred = isItemStarred(item.to)
              return (
                <li key={item.to}>
                  <div
                    className={`w-full rounded-md font-medium transition-all duration-200 text-sm ${
                      isActive
                        ? "bg-gradient-to-r from-sidebar-primary to-primary text-sidebar-primary-foreground shadow-md"
                        : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-sm"
                    } ${isCollapsed ? "flex justify-center items-center" : "flex justify-between items-center"}`}
                  >
                    <Link
                      to={item.to}
                      className={`${isCollapsed ? "px-3 py-1.5" : "flex-1 px-3 py-1.5 text-left"}`}
                      title={isCollapsed ? item.label : undefined}
                    >
                      {isCollapsed ? <span className="text-lg">{emoji || "📄"}</span> : item.label}
                    </Link>
                    {!isCollapsed && (
                      <div className="pr-2">
                        <StarIcon
                          filled={starred}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            handleToggleStar({
                              to: item.to,
                              label: item.label,
                              icon: emoji,
                            })
                          }}
                        />
                      </div>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        </div>

        {/* User Section */}
        <div className="pt-3 mt-3 border-t border-sidebar-border">
          {user ? (
            <div className="space-y-2">
              {!isCollapsed && (
                <div className="text-sm text-sidebar-foreground px-3">
                  <p className="font-medium truncate">{user.user_metadata?.name || user.email}</p>
                  <p className="text-xs truncate">{user.email}</p>
                </div>
              )}
              <div className={`flex items-center ${isCollapsed ? "justify-center" : "justify-between px-3"}`}>
                <button
                  onClick={handleSignOut}
                  className={`${
                    isCollapsed ? "p-2" : "flex-1 mr-2 px-2 py-1.5 text-xs text-left"
                  } text-destructive hover:bg-destructive/10 rounded-md transition-colors`}
                  title={isCollapsed ? "Sign Out" : undefined}
                >
                  {isCollapsed ? "🚪" : "🚪 Sign Out"}
                </button>
                {!isCollapsed && <DarkModeToggle />}
              </div>
              {isCollapsed && (
                <div className="flex justify-center">
                  <DarkModeToggle />
                </div>
              )}
            </div>
          ) : (
            <div className={isCollapsed ? "" : "px-3"}>
              <Link
                to="/auth"
                className={`block w-full ${
                  isCollapsed ? "p-2" : "px-3 py-1.5"
                } text-sm text-center hover:bg-primary/10 rounded-md transition-colors`}
                title={isCollapsed ? "Sign In" : undefined}
              >
                {isCollapsed ? "🔐" : "🔐 Sign In"}
              </Link>
            </div>
          )}
          {!isCollapsed && <p className="text-xs m-0 mt-3 px-3 pb-1">✅ Connected to Edge API</p>}
        </div>
      </div>
    </aside>
  )
}

const APP_NAME = "AIX Ops"

/** Routes where the sidebar hides off-screen and slides out over the page when the cursor reaches the left edge */
const PEEK_SIDEBAR_PATHS = new Set(["/"])
/** Grace period so a brief overshoot past the sidebar's edge doesn't snap it shut */
const PEEK_CLOSE_DELAY_MS = 250

/**
 * Match the current pathname to a route registry entry and set document.title.
 * Handles dynamic segments (e.g. /coach-os/coach/$coachId → "Coach").
 */
function usePageTitle() {
  const location = useLocation()

  useEffect(() => {
    const pathname = location.pathname

    // Find best matching route: convert registry paths to regex patterns
    let bestMatch: { label: string; depth: number } | null = null

    for (const entry of routeRegistry) {
      if (!entry.label) continue
      // Convert "$param" segments to wildcard matchers
      const pattern = entry.path.replace(/\$[^/]+/g, "[^/]+")
      const regex = new RegExp(`^${pattern}$`)
      if (regex.test(pathname)) {
        const depth = entry.path.split("/").length
        if (!bestMatch || depth > bestMatch.depth) {
          bestMatch = { label: entry.label, depth }
        }
      }
    }

    document.title = bestMatch ? `${bestMatch.label} | ${APP_NAME}` : APP_NAME
  }, [location.pathname])
}

// Layout component with collapsible sidebar
function Layout() {
  const { user, loading } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [peeking, setPeeking] = useState(false)
  const peekCloseTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const peekMode = PEEK_SIDEBAR_PATHS.has(location.pathname)

  const openPeek = () => {
    clearTimeout(peekCloseTimer.current)
    setPeeking(true)
  }
  const closePeek = () => {
    clearTimeout(peekCloseTimer.current)
    peekCloseTimer.current = setTimeout(() => setPeeking(false), PEEK_CLOSE_DELAY_MS)
  }

  useEffect(() => {
    setPeeking(false)
    return () => clearTimeout(peekCloseTimer.current)
  }, [location.pathname])

  usePageTitle()

  // Initialize global shortcuts
  useGlobalShortcuts()

  // Load collapsed state from localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed")
    return saved === "true"
  })

  // Save collapsed state to localStorage
  const handleToggleCollapse = () => {
    const newState = !sidebarCollapsed
    setSidebarCollapsed(newState)
    localStorage.setItem("sidebarCollapsed", String(newState))
  }

  // Don't render sidebar if not authenticated
  if (!loading && !user) {
    return (
      <div className="h-screen font-sans bg-background">
        <main className="overflow-auto bg-background">
          <Outlet />
        </main>
        {import.meta.env.DEV && <TanStackRouterDevtools />}
      </div>
    )
  }

  return (
    <div className="flex h-screen font-sans bg-background relative">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Peek mode: an invisible strip on the left edge slides the full sidebar out over the page */}
      {peekMode && (
        <div
          className="fixed inset-y-0 left-0 z-40 hidden w-2 lg:block"
          aria-hidden="true"
          onMouseEnter={openPeek}
          onMouseLeave={closePeek}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 transform ${
          sidebarOpen || (peekMode && peeking) ? "translate-x-0" : "-translate-x-full"
        } ${peekMode ? (peeking ? "lg:shadow-2xl" : "") : "lg:static lg:translate-x-0"} transition-transform duration-200 ease-out`}
        onMouseEnter={peekMode ? openPeek : undefined}
        onMouseLeave={peekMode ? closePeek : undefined}
        onFocus={peekMode ? openPeek : undefined}
        onBlur={peekMode ? closePeek : undefined}
      >
        <Sidebar
          isCollapsed={peekMode ? false : sidebarCollapsed}
          onToggleCollapse={peekMode ? () => setPeeking(false) : handleToggleCollapse}
        />
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-auto bg-background relative">
        {/* Hamburger menu button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute top-4 left-4 z-50 p-2 rounded-md bg-card shadow-md hover:bg-accent lg:hidden flex items-center justify-center"
          aria-label="Toggle main menu"
        >
          {sidebarOpen ? <X className="w-6 h-6 " /> : <Menu className="w-6 h-6 " />}
        </button>
        <Outlet />
      </main>

      {/* Global Command Bar */}
      <AixGlobalCommandBar name="global-nav">
        <NavigationCommandItems />
      </AixGlobalCommandBar>

      {import.meta.env.DEV && <TanStackRouterDevtools />}
      {user && (
        <Suspense fallback={null}>
          <DevModePanel />
        </Suspense>
      )}
    </div>
  )
}

// Root route
const rootRoute = createRootRoute({
  component: Layout,
})

// Individual routes
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => (
    <ProtectedRoute>
      <HomeDashboardScreen />
    </ProtectedRoute>
  ),
})

const myWorkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/my-work",
  component: () => (
    <ProtectedRoute>
      <MyWorkScreen />
    </ProtectedRoute>
  ),
})

const aiChatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ai-chat",
  component: () => (
    <ProtectedRoute>
      <AIChatHubScreen />
    </ProtectedRoute>
  ),
})

const aiProjectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ai-projects",
  component: () => (
    <ProtectedRoute>
      <AIProjectsScreen />
    </ProtectedRoute>
  ),
})

const seoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/seo",
  component: () => (
    <ProtectedRoute>
      <SEOScreen />
    </ProtectedRoute>
  ),
})

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports",
  validateSearch: parseReportsSearch,
  component: () => (
    <ProtectedRoute>
      <ReportsScreen />
    </ProtectedRoute>
  ),
})

const cronRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cron",
  component: () => (
    <ProtectedRoute>
      <CronScreen />
    </ProtectedRoute>
  ),
})

const childrenBooksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/children-books",
  component: () => (
    <ProtectedRoute>
      <ChildrenBooksScreen />
    </ProtectedRoute>
  ),
})

const imageConverterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/image-converter",
  component: () => (
    <PublicRoute>
      <ImageConverter />
    </PublicRoute>
  ),
})

const calendarParserRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/calendar-parser",
  component: () => (
    <ProtectedRoute>
      <CalendarParser />
    </ProtectedRoute>
  ),
})

// Removed todoistRoute - personal project

const metaAdsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/meta-ads",
  validateSearch: (search: any) => {
    return {
      accountId: (search.accountId as string) || undefined,
    }
  },
  component: () => (
    <ProtectedRoute>
      <MetaAdsScreen />
    </ProtectedRoute>
  ),
})

const sportsWordleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sports-wordle",
  component: () => (
    <ProtectedRoute>
      <SportsWordleScreen />
    </ProtectedRoute>
  ),
})

const brainRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/brain",
  component: () => (
    <ProtectedRoute>
      <BrainScreen />
    </ProtectedRoute>
  ),
})

const databrainRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/databrain",
  component: () => (
    <ProtectedRoute>
      <DatabrainScreen />
    </ProtectedRoute>
  ),
})

const diagrammerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/diagrammer",
  component: () => (
    <ProtectedRoute>
      <DiagrammerScreen />
    </ProtectedRoute>
  ),
})

const diagrammerDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/diagrammer/$diagramId",
  component: () => (
    <ProtectedRoute>
      <DiagrammerScreen />
    </ProtectedRoute>
  ),
})

const diagrammerRenderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/diagrammer-render/$diagramId",
  component: () => <DiagrammerScreen renderMode />,
})

const trackablesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/trackables",
  component: () => (
    <ProtectedRoute>
      <TrackablesScreen />
    </ProtectedRoute>
  ),
})

const monitorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/devtools/monitors",
  component: () => (
    <ProtectedRoute>
      <MonitorsScreen />
    </ProtectedRoute>
  ),
})

const scenarioDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/devtools/monitors/$scenarioId",
  component: () => (
    <ProtectedRoute>
      <ScenarioDetailScreen />
    </ProtectedRoute>
  ),
})

const incidentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/devtools/incidents",
  component: () => (
    <ProtectedRoute>
      <IncidentsScreen />
    </ProtectedRoute>
  ),
})

const incidentDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/devtools/incidents/$incidentId",
  component: () => (
    <ProtectedRoute>
      <IncidentDetailScreen />
    </ProtectedRoute>
  ),
})

const onCallRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/devtools/on-call",
  component: () => (
    <ProtectedRoute>
      <OnCallScreen />
    </ProtectedRoute>
  ),
})

const uptimeMonitorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/devtools/uptime",
  component: () => (
    <ProtectedRoute>
      <UptimeMonitorsScreen />
    </ProtectedRoute>
  ),
})

const uptimeMonitorDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/devtools/uptime/$monitorId",
  component: () => (
    <ProtectedRoute>
      <UptimeMonitorDetailScreen />
    </ProtectedRoute>
  ),
})

const productPulseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/product/$company",
  component: () => (
    <ProtectedRoute>
      <ProductPulseScreen />
    </ProtectedRoute>
  ),
})

const vendorStatusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/devtools/vendor-status",
  component: () => (
    <ProtectedRoute>
      <VendorStatusScreen />
    </ProtectedRoute>
  ),
})

const agentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents",
  component: () => (
    <ProtectedRoute>
      <AgentsScreen />
    </ProtectedRoute>
  ),
})

const agentsCockpitRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/cockpit",
  component: () => (
    <ProtectedRoute>
      <AgentsCockpitScreen />
    </ProtectedRoute>
  ),
})

const agentsCockpitRunRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/cockpit/$agentId/run/$runId",
  component: () => (
    <ProtectedRoute>
      <AgentsCockpitScreen />
    </ProtectedRoute>
  ),
})

const agentsRunsHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/runs",
  component: () => (
    <ProtectedRoute>
      <AgentsRunsHistoryScreen />
    </ProtectedRoute>
  ),
})

const evalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/evals",
  component: () => (
    <ProtectedRoute>
      <EvalsScreen />
    </ProtectedRoute>
  ),
})

const deployFeedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/devtools/deploy-feed",
  component: () => (
    <ProtectedRoute>
      <DeployFeedScreen />
    </ProtectedRoute>
  ),
})

const secretsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/devtools/secrets",
  component: () => (
    <ProtectedRoute>
      <SecretsScreen />
    </ProtectedRoute>
  ),
})

const statusPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/status",
  component: () => (
    <PublicRoute>
      <StatusPage />
    </PublicRoute>
  ),
})

const coachOSRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/coach-os",
  component: () => (
    <ProtectedRoute>
      <CoachOSScreen />
    </ProtectedRoute>
  ),
})

const coachDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/coach-os/coach/$coachId",
  component: () => (
    <ProtectedRoute>
      <CoachDetailScreen />
    </ProtectedRoute>
  ),
})

const coachTemplateDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/coach-os/templates/$templateId",
  component: () => (
    <ProtectedRoute>
      <CoachTemplateDetailScreen />
    </ProtectedRoute>
  ),
})

const weeklyReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/coach-os/weekly-review",
  component: () => (
    <ProtectedRoute>
      <WeeklyReviewScreen />
    </ProtectedRoute>
  ),
})

// Removed contextRoute - planning feature

const integrationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/integrations",
  component: () => (
    <ProtectedRoute>
      <IntegrationsScreen />
    </ProtectedRoute>
  ),
})

const metricsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/metrics",
  component: () => (
    <ProtectedRoute>
      <MetricsScreen />
    </ProtectedRoute>
  ),
})

const scrapingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scraping",
  validateSearch: (search: any) => {
    return {
      tab: (search.tab as string) || undefined,
      monitorSlug: (search.monitorSlug as string) || undefined,
      jobId: (search.jobId as string) || undefined,
    }
  },
  component: () => (
    <ProtectedRoute>
      <ScrapingScreen />
    </ProtectedRoute>
  ),
})

const musicRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/music",
  component: () => (
    <ProtectedRoute>
      <MusicCatalogScreen />
    </ProtectedRoute>
  ),
})

const mashupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mashup",
  component: () => (
    <ProtectedRoute>
      <MashupScreen />
    </ProtectedRoute>
  ),
})

const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/notifications",
  component: () => (
    <ProtectedRoute>
      <NotificationScreen />
    </ProtectedRoute>
  ),
})

const userManagementRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/user-management",
  component: () => (
    <ProtectedRoute>
      <UserManagementScreen />
    </ProtectedRoute>
  ),
})

const auditLogsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/audit-logs",
  component: () => (
    <ProtectedRoute>
      <AuditLogsScreen />
    </ProtectedRoute>
  ),
  validateSearch: (search: any) => ({
    action: (search.action as string) || undefined,
    entityType: (search.entityType as string) || undefined,
    userId: (search.userId as string) || undefined,
    entityId: (search.entityId as string) || undefined,
  }),
})

const electronNativeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/electron-native",
  component: () => (
    <ProtectedRoute>
      <ElectronNativeScreen />
    </ProtectedRoute>
  ),
})

const toolsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tools",
  component: () => (
    <ProtectedRoute>
      <Tools />
    </ProtectedRoute>
  ),
})

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: () => (
    <ProtectedRoute>
      <SettingsScreen />
    </ProtectedRoute>
  ),
})

// Removed bansalLabsRoute - personal project

const bookReaderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/book/$bookId",
  component: () => (
    <ProtectedRoute>
      <BookReaderScreen />
    </ProtectedRoute>
  ),
})

// Auth routes (public)
const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth",
  component: AuthScreen,
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
})

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/callback",
  component: AuthCallbackScreen,
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
})

// CLI authorization (public) — hands a short-lived MFA'd session to `aix-token login`.
const cliAuthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cli-auth",
  component: CliAuthScreen,
  validateSearch: (search: any) => ({
    port: (search.port as string) || undefined,
  }),
})

const adminSigninRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/signin",
  component: AdminSigninScreen,
})

const slackCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/slack/callback",
  component: SlackCallbackScreen,
})

const newsletterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/newsletter",
  component: () => (
    <ProtectedRoute>
      <NewsletterScreen />
    </ProtectedRoute>
  ),
})

const newsletterEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/newsletter/$id",
  component: () => (
    <ProtectedRoute>
      <NewsletterEditor />
    </ProtectedRoute>
  ),
})

const contentIngestionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/content-ingestion",
  component: () => (
    <ProtectedRoute>
      <ContentIngestionScreen />
    </ProtectedRoute>
  ),
})

const contentManagementRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/content-management",
  component: () => (
    <ProtectedRoute>
      <ContentManagementScreen />
    </ProtectedRoute>
  ),
  validateSearch: (search: any) => ({
    tab: (search.tab as string) || undefined,
    brand: (search.brand as string) || undefined,
  }),
})

// CRM Routes
const crmRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm",
  component: () => (
    <ProtectedRoute>
      <CRMLayout>
        <CRMDashboard />
      </CRMLayout>
    </ProtectedRoute>
  ),
})

const crmCampaignsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/campaigns",
  component: () => (
    <ProtectedRoute>
      <CRMLayout>
        <CampaignsList />
      </CRMLayout>
    </ProtectedRoute>
  ),
})

const crmContactsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/contacts",
  component: () => (
    <ProtectedRoute>
      <CRMLayout>
        <ContactsList />
      </CRMLayout>
    </ProtectedRoute>
  ),
})

const crmInboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/inbox",
  component: () => (
    <ProtectedRoute>
      <CRMLayout>
        <ConversationInbox />
      </CRMLayout>
    </ProtectedRoute>
  ),
})

const crmStartConversationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/start-conversation",
  component: () => (
    <ProtectedRoute>
      <CRMLayout>
        <StartConversation />
      </CRMLayout>
    </ProtectedRoute>
  ),
  validateSearch: (search: any) => ({
    contactId: (search.contactId as string) || undefined,
  }),
})

const crmTemplatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/templates",
  component: () => (
    <ProtectedRoute>
      <CRMLayout>
        <TemplatesLibrary />
      </CRMLayout>
    </ProtectedRoute>
  ),
})

const crmAnalyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/analytics",
  component: () => (
    <ProtectedRoute>
      <CRMLayout>
        <Analytics />
      </CRMLayout>
    </ProtectedRoute>
  ),
})

const crmCampaignDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/campaigns/$id",
  component: () => (
    <ProtectedRoute>
      <CRMLayout>
        <CampaignDetail />
      </CRMLayout>
    </ProtectedRoute>
  ),
})

const crmContactDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/contacts/$id",
  component: () => (
    <ProtectedRoute>
      <CRMLayout>
        <ContactDetail />
      </CRMLayout>
    </ProtectedRoute>
  ),
})

const crmConversationDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/inbox/$id",
  component: () => (
    <ProtectedRoute>
      <CRMLayout>
        <ConversationDetail />
      </CRMLayout>
    </ProtectedRoute>
  ),
})

const crmEmailAccountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/email-accounts",
  component: () => (
    <ProtectedRoute>
      <CRMLayout>
        <EmailAccounts />
      </CRMLayout>
    </ProtectedRoute>
  ),
})

const accessDeniedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/access-denied",
  component: AccessDeniedScreen,
})

const signOutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signout",
  component: SignOutScreen,
})

const moviesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/movies",
  component: () => (
    <ProtectedRoute>
      <MoviesScreen />
    </ProtectedRoute>
  ),
  validateSearch: (search: any) => ({
    tab: (search.tab as string) || undefined,
    genre: (search.genre as string) || undefined,
    year: (search.year as string) || undefined,
  }),
})

const emailMarketingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/email-marketing",
  component: () => (
    <ProtectedRoute>
      <EmailMarketingScreen />
    </ProtectedRoute>
  ),
})

const metaAdsCampaignDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/meta-ads/campaigns/$campaignId",
  component: () => (
    <ProtectedRoute>
      <div>Meta Ads Campaign Detail - Campaign ID: {/* Add campaign detail component here */}</div>
    </ProtectedRoute>
  ),
})

const multiLanguageTestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/multi-language-test",
  component: () => (
    <ProtectedRoute>
      <MultiLanguageTestAppScreen />
    </ProtectedRoute>
  ),
})

const easDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/eas-dashboard",
  component: () => (
    <ProtectedRoute>
      <EASDashboardScreen />
    </ProtectedRoute>
  ),
})

const easProjectDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/eas-dashboard/$projectSlug",
  component: () => (
    <ProtectedRoute>
      <ProjectDashboard />
    </ProtectedRoute>
  ),
})

const onboardingJourneysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding/journeys",
  component: () => (
    <ProtectedRoute>
      <JourneysListScreen />
    </ProtectedRoute>
  ),
})

const onboardingJourneyDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding/journey/$journeyId",
  component: () => (
    <ProtectedRoute>
      <JourneyDetailScreen />
    </ProtectedRoute>
  ),
})

const onboardingVideoPlayerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding/video/$videoId",
  component: () => (
    <ProtectedRoute>
      <VideoPlayerScreen />
    </ProtectedRoute>
  ),
})

const onboardingAdminVideosRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding/admin/videos",
  component: () => (
    <ProtectedRoute>
      <AdminVideosScreen />
    </ProtectedRoute>
  ),
})

const onboardingAdminJourneysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding/admin/journeys",
  component: () => (
    <ProtectedRoute>
      <AdminJourneysScreen />
    </ProtectedRoute>
  ),
})

const onboardingAdminQuestionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding/admin/questions",
  component: () => (
    <ProtectedRoute>
      <AdminQuestionsScreen />
    </ProtectedRoute>
  ),
})

// Companies Routes
const companiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/companies",
  component: () => (
    <ProtectedRoute>
      <CompaniesScreen />
    </ProtectedRoute>
  ),
})

const companyDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/companies/$slug",
  component: () => (
    <ProtectedRoute>
      <CompanyDetailScreen />
    </ProtectedRoute>
  ),
})

const aiGatewayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ai-gateway",
  component: () => (
    <ProtectedRoute>
      <AiGatewayScreen />
    </ProtectedRoute>
  ),
})

const llmPlaygroundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/llm-playground",
  component: () => (
    <ProtectedRoute>
      <LLMPlaygroundScreen />
    </ProtectedRoute>
  ),
})

// Aura Builder Routes
const auraRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/aura",
  component: () => (
    <ProtectedRoute>
      <AuraScreen />
    </ProtectedRoute>
  ),
})

const auraEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/aura/editor/$projectId",
  component: () => (
    <ProtectedRoute>
      <AuraEditorScreen />
    </ProtectedRoute>
  ),
})

const auraEditorPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/aura/editor/$projectId/$pageSlug",
  component: () => (
    <ProtectedRoute>
      <AuraEditorScreen />
    </ProtectedRoute>
  ),
})

const auraTemplatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/aura/templates",
  component: () => (
    <ProtectedRoute>
      <AuraTemplatesScreen />
    </ProtectedRoute>
  ),
})

const auraTemplateBuilderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/aura/template-builder",
  component: () => (
    <ProtectedRoute>
      <AuraTemplateBuilderScreen />
    </ProtectedRoute>
  ),
})

const shraysHobbiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/shrays-hobbies",
  component: () => (
    <ProtectedRoute>
      <ShraysHobbiesScreen />
    </ProtectedRoute>
  ),
})

// LinkPacks Routes (public — non-authenticated people can open shared packs)
const linkPacksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/linkpacks",
  component: () => (
    <PublicRoute>
      <LinkPacksScreen />
    </PublicRoute>
  ),
})

const linkPackDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/linkpacks/$slug",
  component: () => (
    <PublicRoute>
      <LinkPackDetailScreen />
    </PublicRoute>
  ),
})

// Sentry test route - throws an error to verify Sentry is working
const SentryBreakInner = () => {
  useEffect(() => {
    throw new Error("Sentry test error from AIX Command Center")
  }, [])
  return <></>
}

const sentryBreakRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sentry-break",
  component: () => (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-4">Sentry Break Test</h1>
      <p className="mb-4">This page throws an error to test Sentry integration.</p>
      <Sentry.ErrorBoundary fallback={<p className="text-red-500">Error was captured by Sentry!</p>}>
        <SentryBreakInner />
      </Sentry.ErrorBoundary>
    </div>
  ),
})

// Create the route tree
const routeTree = rootRoute.addChildren([
  indexRoute,
  myWorkRoute,
  aiChatRoute,
  aiProjectsRoute,
  newsletterRoute,
  newsletterEditorRoute,
  contentIngestionRoute,
  contentManagementRoute,
  crmRoute,
  crmCampaignsRoute,
  crmCampaignDetailRoute,
  crmContactsRoute,
  crmContactDetailRoute,
  crmInboxRoute,
  crmStartConversationRoute,
  crmConversationDetailRoute,
  crmTemplatesRoute,
  crmAnalyticsRoute,
  crmEmailAccountsRoute,
  seoRoute,
  reportsRoute,
  cronRoute,
  childrenBooksRoute,
  imageConverterRoute,
  calendarParserRoute,
  metaAdsRoute,
  metaAdsCampaignDetailRoute,
  sportsWordleRoute,

  integrationsRoute,
  metricsRoute,
  scrapingRoute,
  musicRoute,
  mashupRoute,
  notificationsRoute,
  userManagementRoute,
  auditLogsRoute,
  electronNativeRoute,
  toolsRoute,
  settingsRoute,

  brainRoute,
  databrainRoute,
  diagrammerRoute,
  diagrammerDetailRoute,
  diagrammerRenderRoute,
  trackablesRoute,
  coachOSRoute,
  coachDetailRoute,
  coachTemplateDetailRoute,
  weeklyReviewRoute,
  bookReaderRoute,
  authRoute,
  authCallbackRoute,
  cliAuthRoute,
  adminSigninRoute,
  accessDeniedRoute,
  signOutRoute,
  slackCallbackRoute,
  moviesRoute,
  emailMarketingRoute,
  multiLanguageTestRoute,
  easDashboardRoute,
  easProjectDashboardRoute,
  onboardingJourneysRoute,
  onboardingJourneyDetailRoute,
  onboardingVideoPlayerRoute,
  onboardingAdminVideosRoute,
  onboardingAdminJourneysRoute,
  onboardingAdminQuestionsRoute,
  companiesRoute,
  companyDetailRoute,
  llmPlaygroundRoute,
  aiGatewayRoute,
  auraRoute,
  auraEditorRoute,
  auraEditorPageRoute,
  auraTemplatesRoute,
  auraTemplateBuilderRoute,
  monitorsRoute,
  scenarioDetailRoute,
  incidentsRoute,
  incidentDetailRoute,
  onCallRoute,
  uptimeMonitorsRoute,
  uptimeMonitorDetailRoute,
  productPulseRoute,
  vendorStatusRoute,
  agentsRoute,
  agentsCockpitRoute,
  agentsCockpitRunRoute,
  agentsRunsHistoryRoute,
  evalsRoute,
  deployFeedRoute,
  secretsRoute,
  statusPageRoute,
  shraysHobbiesRoute,
  linkPacksRoute,
  linkPackDetailRoute,
  sentryBreakRoute,
])

// Create the router
// @ts-ignore
export const router = createRouter({ routeTree })

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}
